import { ArrowRightIcon } from '@heroicons/react/solid';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import { APPLICATION_URL } from '../../constants';
import AvenueGatsbyImage from '../common/AvenueGatsbyImage';
import RemoteText from '../common/remoteText';
import AlertBanners from './AlertBanners';
import NavItem from './NavItem';

function CustomNavbar({ hideNavItems, applyUrl, preventLogoLink }) {
    const { contentfulNavigation, avenueDesktopLogo, avenueMobileLogo } = useStaticQuery(graphql`
        {
            contentfulNavigation(name: { eq: "Header Navigation" }) {
                name
                items {
                    id
                    title
                    url
                }
            }
            avenueDesktopLogo: contentfulSiteImages(name: { eq: "AvenueWhiteDesktopLogo" }) {
                name
                id
                imageFile {
                    gatsbyImageData
                    localFile {
                        publicURL
                        extension
                    }
                }
            }
            avenueMobileLogo: contentfulSiteImages(name: { eq: "AvenueWhiteMobileLogo" }) {
                name
                id
                imageFile {
                    gatsbyImageData
                    localFile {
                        publicURL
                        extension
                    }
                }
            }
        }
    `);

    const [showMobileMenu, setShowMobileMenu] = React.useState(true);

    let headerNav = null;

    if (contentfulNavigation && Array.isArray(contentfulNavigation.items) && !hideNavItems) {
        headerNav = contentfulNavigation.items.map((item) => {
            if (!item) {
                return null;
            }

            const { id, title, url } = item;

            if (title === 'Home') {
                return <NavItem key={`top-nav-${id}`} title={title} to='/' />;
            }

            return <NavItem key={`top-nav-${id}`} title={title} to={`/${url}`} />;
        });
    }

    return (
        <>
            <div>
                <nav className='fixed z-50 w-full bg-avenue-slate-500 text-white'>
                    <AlertBanners />
                    <div className='container mx-auto'>
                        <div className='relative flex h-24 items-center justify-between px-6'>
                            <div className='absolute inset-y-0 right-3 flex items-center xl:hidden'>
                                <button
                                    type='button'
                                    className='focus:ring-none inline-flex items-center justify-center rounded-md p-2 focus:outline-none focus:ring-0'
                                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                                >
                                    <span className='sr-only'>Open main menu</span>
                                    <svg
                                        className='block h-6 w-6'
                                        xmlns='http://www.w3.org/2000/svg'
                                        fill='none'
                                        viewBox='0 0 24 24'
                                        stroke='currentColor'
                                        aria-hidden='true'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M4 6h16M4 12h16M4 18h16'
                                        />
                                    </svg>

                                    <svg
                                        className='hidden h-6 w-6'
                                        xmlns='http://www.w3.org/2000/svg'
                                        fill='none'
                                        viewBox='0 0 24 24'
                                        stroke='currentColor'
                                        aria-hidden='true'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M6 18L18 6M6 6l12 12'
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div className='flex w-full flex-row items-center justify-between'>
                                <div className='hidden flex-shrink xl:flex'>
                                    {preventLogoLink && (
                                        <AvenueGatsbyImage
                                            localFile={avenueDesktopLogo?.imageFile?.localFile}
                                            title='Avenue Bank Logo'
                                            className='max-h-8 w-fit'
                                        />
                                    )}
                                    {!preventLogoLink && (
                                        <a href='/'>
                                            <AvenueGatsbyImage
                                                localFile={avenueDesktopLogo?.imageFile?.localFile}
                                                title='Avenue Bank Logo'
                                                className='max-h-8 w-fit'
                                            />
                                        </a>
                                    )}
                                </div>

                                <div className='flex flex-grow xl:hidden'>
                                    {preventLogoLink && (
                                        <AvenueGatsbyImage
                                            localFile={avenueMobileLogo?.imageFile?.localFile}
                                            title='Avenue Bank Logo'
                                            className='max-h-8 w-fit'
                                        />
                                    )}
                                    {!preventLogoLink && (
                                        <a href='/'>
                                            <AvenueGatsbyImage
                                                localFile={avenueMobileLogo?.imageFile?.localFile}
                                                title='Avenue Bank Logo'
                                                className='max-h-8 w-fit'
                                            />
                                        </a>
                                    )}
                                </div>

                                <div className='hidden flex-grow flex-row items-center justify-center space-x-4 xl:flex'>
                                    {headerNav}
                                </div>
                                <div className='hidden flex-shrink items-end xl:flex'>
                                    <a
                                        id='apply-now-btn'
                                        href={applyUrl || APPLICATION_URL}
                                        className='max-width:100px display:block w-full rounded-md bg-avenue-orange-500 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-avenue-blue-600 md:w-auto'
                                        target='_self'
                                        rel='noreferrer'
                                    >
                                        <RemoteText name='topNavBtnCTA' />
                                        <span className='tracking-normal text-white'> →</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='relative z-50 h-screen w-screen xl:hidden' hidden={showMobileMenu}>
                        <div className='z-50 grid grid-cols-1 space-y-1 px-6 pb-3 pt-2 text-white'>{headerNav}</div>
                        <div className='bottom-20 flex w-screen justify-center px-6 pb-3 pt-2 text-white xs:relative xs:bottom-0'>
                            <a
                                id='apply-now-btn'
                                href={applyUrl || APPLICATION_URL}
                                className='max-width:100px display:block block w-fit rounded-md bg-avenue-orange-500 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-avenue-blue-600'
                                aria-label='Link to apply now'
                                target='_self'
                                rel='noreferrer'
                            >
                                <RemoteText name='topNavBtnCTA' />
                                <ArrowRightIcon className='relative ml-1 inline-block h-5 w-5 stroke-current' />
                            </a>
                        </div>
                    </div>
                </nav>
            </div>
            <div id='back-top' />
        </>
    );
}

export default CustomNavbar;
